import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "store";
import { UPDATE_AUTH } from "store/actionTypes";
import SHOW_TOAST, { SHOW_INTERNET_TOAST } from "./showToast";

/**
 * @var status_code
 * @description Defined all static API status Code
 */
export const status_code = {
  success: 200,
  invalid: 400,
  timeout: 408,
  notFound: 204,
  badRequest: 400,
  userDelete: 410,
  serverError: 500,
  Unauthorized: 401,
  successAction: 201,
};

const successCodes = [200, 201];
const sessionExpiryCodes = [403, 401];

/**
 * create axios instance
 */
const $http = axios.create({
  baseURL: `${process.env.REACT_APP_DOMAIN}`,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

$http.interceptors.request.use(async (config: any) => {
  if (config.headers) {
    const getState = store?.getState();
    if (getState) {
      const { token = "" } = getState.AuthReducer;
      if (token && token.length > 0) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    }
    return config;
  }
  return config;
});

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onErrorResponse = (error: AxiosError | Error): Promise<AxiosError> => {
  if (axios.isAxiosError(error)) {
    const { statusText, status, data } =
      (error.response as AxiosResponse) ?? {};
    switch (status) {
      case 400: {
        break;
      }
      case 401: {
        sessionExpireHandler();
        // const myStore: ReducerModal = store.getState();
        // console.log('myStore', myStore);
        // if (
        //   myStore?.authReducer?.otpVerificationResponseData?.data?.accessToken
        // ) {
        //   chatServiceRef?.current?.disconnect();
        //   store.dispatch(authAction.removeOtpData({}));
        //   store.dispatch(authAction.logout({}));
        //   store.dispatch(chatAction.logout({}));
        //   navigationRef?.current?.reset({
        //     index: 0,
        //     routes: [{ name: screenNames.LOGIN_SCREEN }],
        //   });
        //   if (snackBarRef && snackBarRef.current && data?.message) {
        //     snackBarRef.current?.showToast({
        //       type: ToastOptionType.ERROR,
        //       message: 'UNAUTHORIZED ACCESS',
        //     });
        //   }
        // Snackbar.show({
        //   text: 'UNAUTHORIZED ACCESS',
        //   duration: Snackbar.LENGTH_LONG,
        //   backgroundColor: '#cc4b37',
        // });
        // }
        console.log("401 || UNAUTHORIZED ACCESS");
        break;
      }
      case 403: {
        console.log("403 || ERROR");
        break;
      }
      case 404: {
        console.log("404 || API NOT FOUND");
        break;
      }
      case 500: {
        console.log("500 || SERVER ERROR");
        break;
      }
      default: {
        console.log("Network Error");
        break;
      }
    }
  }
  return Promise.reject(error);
};

$http.interceptors.response.use(onResponse, onErrorResponse);

const sessionExpireHandler = () => {
  store.dispatch({ type: UPDATE_AUTH, payload: {} });
  sessionStorage.setItem("token", "");
};

/**
 * post api call common function
 * @param endPoint endpoint of api
 * @param params data to send
 * @param callback callback function
 */
export const POST_API_CALL = (
  endPoint: string,
  params: object | Array<Object>,
  callback: Function
) => {
  $http
    .post(endPoint, params)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = error?.response?.data?.message;
        if (Array.isArray(message)) {
          message = message[0];
        }

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST();
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const PUT_API_CALL = (
  endPoint: string,
  params: object,
  callback: Function
) => {
  $http
    .put(endPoint, params)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = error?.response?.data?.error;
        if (Array.isArray(message)) {
          message = message[0];
        }

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST();
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const GET_API_CALL = (
  endPoint: string,
  params: string,
  callback: Function
) => {
  $http
    .get(endPoint + params, {})
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = error?.response?.data?.error;
        if (Array.isArray(message)) {
          message = message[0];
        }

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST();
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const DELETE_API_CALL = (endPoint: string, callback: Function) => {
  $http
    .delete(endPoint)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = error?.response?.data?.error;
        if (Array.isArray(message)) {
          message = message[0];
        }

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST();
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

/**
 * patch api call common function
 * @param endPoint endpoint of api
 * @data data to send
 * @param callback callback function
 */
export const PATCH_API_CALL = (
  endPoint: string,
  data: any,
  callback: Function
) => {
  $http
    .patch(endPoint, data)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = error?.response?.data?.error;
        if (Array.isArray(message)) {
          message = message[0];
        }

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST();
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};
